import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  ShieldCheck,
} from "lucide-react";
import {
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  copyContactEmail,
} from "@/lib/contactEmail";
import { trackProductEvent } from "@/lib/productAnalytics";

const missingItems = [
  {
    title: "Hydroizolacja",
    desc: "Cz\u0119sto pomijana przy szybkiej wycenie \u0142azienki. Sprawd\u017a, czy zakres obejmuje izolacj\u0119, ta\u015bmy i naro\u017cniki.",
  },
  {
    title: "Szlify 45\u00b0",
    desc: "\u0141atwo o nich zapomnie\u0107 przy naro\u017cnikach, p\u00f3\u0142kach i estetycznym wyko\u0144czeniu p\u0142ytek.",
  },
  {
    title: "Akrylowanie",
    desc: "Drobna pozycja, ale przy ca\u0142ym mieszkaniu potrafi zje\u015b\u0107 czas i materia\u0142.",
  },
];

const steps = [
  "Wpisz metra\u017c i zakres",
  "Sprawd\u017a pozycje do decyzji",
  "Wygeneruj PDF dla klienta",
];

const benefitCards = [
  {
    icon: ClipboardCheck,
    title: "Szybka wycena",
    desc: "Wpisz metra\u017c, region i zakres prac. Dostajesz roboczy wynik do sprawdzenia.",
  },
  {
    icon: ShieldCheck,
    title: "Baza robocza ProWycena",
    desc: "System podpowiada orientacyjne stawki regionalne. Ty decydujesz, ile wpisujesz do oferty.",
  },
  {
    icon: FileText,
    title: "PDF dla klienta",
    desc: "Na ko\u0144cu generujesz czyst\u0105 ofert\u0119, bez wewn\u0119trznych alert\u00f3w i bez pokazywania mar\u017cy.",
  },
];

const heroChecklist = [
  "gruntowanie",
  "folie i ta\u015bmy",
  "akryl",
  "odpady / sprz\u0105tanie",
];

const heroPdfRows = [
  { item: "Malowanie 2x", amount: "1 456 z\u0142" },
  { item: "Materia\u0142y", amount: "po stronie klienta" },
  { item: "Oferta", amount: "do potwierdzenia" },
];

const Landing = () => {
  const [copyMessage, setCopyMessage] = useState("");

  const handleCopyContactEmail = async (): Promise<void> => {
    const copied = await copyContactEmail(navigator.clipboard);
    setCopyMessage(
      copied
        ? "Skopiowano adres e-mail"
        : "Nie udało się skopiować. Zaznacz adres i skopiuj ręcznie."
    );
  };

  return (
    <div className="min-h-screen bg-[#0b0d10] text-slate-50">
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#0b0d10]/95">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/" className="text-xl font-black tracking-tight text-white">
            ProWycena<span className="text-amber-400">app.pl</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/app/wycena"
              className="hidden min-h-11 items-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-black text-[#07110b] transition hover:bg-emerald-400 sm:inline-flex"
            >
              {"Zr\u00f3b wycen\u0119"}
            </Link>
            <Link
              to="/login"
              className="min-h-11 rounded-md border border-white/15 bg-[#11151b] px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-amber-400/60 hover:text-amber-200"
            >
              {"Zaloguj si\u0119"}
            </Link>
          </div>
        </div>
      </header>

      <section className="border-b border-white/10 bg-[#0d1014]">
        <div className="container relative mx-auto grid gap-8 px-4 py-12 md:grid-cols-[0.92fr_1.08fr] md:items-center md:py-16">
          <div>
            <div className="mb-5 inline-flex rounded-sm border border-amber-400/45 bg-[#15191f] px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-amber-200">
              {"TESTY DLA EKIP WYKO\u0143CZENIOWYCH"}
            </div>
            <h1 className="max-w-4xl text-4xl font-black leading-tight tracking-tight md:text-6xl">
              {"Program do szybkiej wyceny remontu dla wykonawc\u00f3w"}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 md:text-xl">
              {"Wpisz metra\u017c, wybierz zakres, sprawd\u017a pozycje z Tarczy Mar\u017cy i wygeneruj ofert\u0119 PDF dla klienta."}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/app/wycena"
                onClick={() => trackProductEvent('pilot_cta_clicked')}
                className="inline-flex min-h-14 items-center justify-center rounded-md bg-emerald-500 px-7 text-base font-black text-[#07110b] transition hover:bg-emerald-400"
              >
                {"Zr\u00f3b pierwsz\u0105 wycen\u0119"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#pdf-preview"
                className="inline-flex min-h-14 items-center justify-center rounded-md border border-amber-400/45 bg-[#15191f] px-7 text-base font-black text-amber-100 transition hover:bg-[#1b2028]"
              >
                {"Zobacz przyk\u0142ad PDF"}
              </a>
            </div>
            <p className="mt-4 text-sm text-slate-400">
              {"Pierwsz\u0105 wycen\u0119 i roboczy PDF testowy mo\u017cesz zrobi\u0107 bez logowania. Zaloguj si\u0119 tylko wtedy, gdy chcesz zapisa\u0107 projekt i wr\u00f3ci\u0107 do niego p\u00f3\u017aniej."}
            </p>
            <p className="mt-5 max-w-2xl border-l-2 border-amber-400/70 pl-4 text-sm font-semibold leading-6 text-slate-300">
              {"Dla ekip wyko\u0144czeniowych, malarzy, glazurnik\u00f3w i ma\u0142ych firm remontowych. Nie zast\u0119puje wykonawcy. Przypomina, czego nie pomin\u0105\u0107."}
            </p>
          </div>

          <div className="border border-[#2a2f38] bg-[#11151b] p-3">
            <div className="border border-[#2a2f38] bg-[#0b0d10]">
              <div className="flex items-center justify-between gap-3 border-b border-[#2a2f38] px-4 py-3">
                <span className="text-xs font-black uppercase tracking-[0.16em] text-slate-300">
                  PROWYCENA / NOWA WYCENA
                </span>
                <span className="border border-emerald-400/40 bg-emerald-400/10 px-2 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-emerald-200">
                  PDF dla klienta: gotowy
                </span>
              </div>

              <div className="grid gap-0 md:grid-cols-[1.08fr_0.92fr]">
                <div className="border-b border-[#2a2f38] p-4 md:border-b-0 md:border-r">
                  <div className="grid gap-2 text-sm">
                    {[
                      ["Obiekt", "pok\u00f3j 18 m\u00b2"],
                      ["Powierzchnia robocza", "ok. 52 m\u00b2"],
                      ["Zakres", "malowanie 2x"],
                      ["Region", "Warmi\u0144sko-Mazurskie"],
                    ].map(([label, value]) => (
                      <div key={label} className="grid grid-cols-[96px_1fr] border-b border-white/10 pb-2 last:border-0">
                        <span className="text-slate-500">{label}:</span>
                        <strong className="text-slate-100">{value}</strong>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 border border-[#2a2f38] bg-[#11151b]">
                    <div className="border-b border-[#2a2f38] px-3 py-2 text-xs font-black uppercase tracking-[0.14em] text-slate-400">
                      Wycena
                    </div>
                    <div className="grid gap-0 text-sm">
                      <div className="flex justify-between border-b border-white/10 px-3 py-2">
                        <span className="text-slate-400">Stawka robocizny</span>
                        <strong className="text-white">{"28 z\u0142/m\u00b2"}</strong>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-slate-400">Suma robocizny</span>
                        <strong className="text-white">{"1 456 z\u0142"}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 border border-amber-400/35 bg-[#14120d]">
                    <div className="border-b border-amber-400/25 px-3 py-2">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-amber-200">{"Tarcza Mar\u017cy"}</p>
                      <p className="mt-1 text-xs text-slate-400">{"Do sprawdzenia przed wys\u0142aniem:"}</p>
                    </div>
                    <ul className="divide-y divide-amber-400/15 text-sm text-slate-100">
                      {heroChecklist.map((item) => (
                        <li key={item} className="flex items-center gap-3 px-3 py-2">
                          <span className="flex h-4 w-4 items-center justify-center border border-amber-300/70 text-[10px] font-black text-amber-200">
                            !
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-4 border border-amber-400/30 bg-amber-400/10 p-3">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                      {"Mo\u017cliwa warto\u015b\u0107 pozycji do sprawdzenia"}
                    </p>
                    <p className="mt-1 text-3xl font-black text-amber-200">{"1 146 z\u0142"}</p>
                  </div>
                </div>

                <div className="bg-[#f8fafc] p-4 text-slate-950">
                  <div className="border border-slate-300 bg-white p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Oferta wykonawcy</p>
                    <h2 className="mt-2 text-xl font-black">Malowanie pokoju</h2>
                    <table className="mt-4 w-full border-collapse text-sm">
                      <tbody>
                        {heroPdfRows.map((row) => (
                          <tr key={row.item} className="border-b border-slate-200">
                            <td className="py-2 text-slate-600">{row.item}</td>
                            <td className="py-2 text-right font-bold">{row.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-4 border-t-2 border-slate-900 pt-3">
                      <div className="flex justify-between text-base font-black">
                        <span>Suma robocza</span>
                        <span>{"1 456 z\u0142"}</span>
                      </div>
                    </div>
                    <p className="mt-4 border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
                      {"Wycena robocza do potwierdzenia przed rozpocz\u0119ciem prac."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0b0d10] py-10">
        <div className="container mx-auto px-4">
          <div className="grid gap-3 text-sm md:grid-cols-3">
            {[
              "Dla ekip wyko\u0144czeniowych",
              "Zdj\u0119cie jako kontekst, nie miarka",
              "PDF jako efekt ko\u0144cowy",
            ].map((item) => (
              <div key={item} className="border border-[#2a2f38] bg-[#11151b] px-4 py-3 font-semibold text-slate-300">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-extrabold md:text-3xl">
              {"Najcz\u0119stsze pozycje, kt\u00f3re uciekaj\u0105 z wyceny"}
            </h2>
            <p className="mt-4 text-slate-400">
              {"Szybka wycena jest dobra tylko wtedy, gdy nie pomijasz drobiazg\u00f3w, kt\u00f3re p\u00f3\u017aniej robisz z w\u0142asnej kieszeni. ProWycena pokazuje pozycje do sprawdzenia przed wys\u0142aniem oferty."}
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {missingItems.map((card) => (
              <div
                key={card.title}
                className="border border-amber-400/20 bg-[#15191f] p-6 transition hover:border-amber-400/45"
              >
                <div className="mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-300" />
                  <h3 className="text-lg font-bold text-white">{card.title}</h3>
                </div>
                <p className="text-sm leading-6 text-slate-300">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#11151b] py-14 md:py-20">
        <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="mb-3 text-sm font-black uppercase tracking-[0.2em] text-amber-200">{"Przyk\u0142ad testowy"}</p>
            <h2 className="text-2xl font-extrabold md:text-3xl">{"Przyk\u0142ad: malowanie 25 m\u00b2"}</h2>
            <p className="mt-4 text-slate-300">
              {"W przyk\u0142adzie testowym Tarcza Mar\u017cy wskaza\u0142a pozycje do sprawdzenia: gruntowanie, zabezpieczenie foli\u0105 i ta\u015bmami oraz odpady / sprz\u0105tanie."}
            </p>
            <p className="mt-4 border border-amber-400/30 bg-amber-400/10 p-4 text-lg font-black text-amber-100">
              {"Mo\u017cliwa warto\u015b\u0107 pozycji do sprawdzenia: 1 146 z\u0142"}
            </p>
            <p className="mt-4 text-sm text-slate-400">
              {"To nie s\u0105 automatycznie doliczone koszty. Wykonawca decyduje, co trafia do oferty."}
            </p>
          </div>
          <div className="border border-white/10 bg-[#0b0d10] p-4">
            <div className="space-y-3">
              {["Gruntowanie pod\u0142o\u017ca", "Zabezpieczenie foli\u0105 i ta\u015bmami", "Odpady / sprz\u0105tanie"].map((item) => (
                <div key={item} className="flex items-center gap-3 border border-white/10 bg-white/5 p-4">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" />
                  <span className="font-semibold text-slate-100">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold md:text-3xl">{"Jak dzia\u0142a ProWycena"}</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step} className="border border-white/10 bg-[#15191f] p-6">
                <span className="mb-4 inline-flex h-10 w-10 items-center justify-center bg-amber-400 text-sm font-black text-[#0b0d10]">
                  {index + 1}
                </span>
                <p className="text-lg font-black text-white">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#11151b] py-14 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold md:text-3xl">Dla kogo jest ProWycena</h2>
          <p className="mx-auto mt-4 max-w-3xl text-center text-slate-400">
            Dla wykonawców, którzy sami wyceniają swoją robotę: ekip wykończeniowych, malarzy, glazurników,
            brukarzy i małych firm remontowych. ProWycena nie zastępuje wykonawcy — porządkuje wycenę
            i przypomina o pozycjach, które łatwo pominąć.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold md:text-3xl">Co policzysz teraz</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {[
              ['Malowanie i gładź', 'Jedno pomieszczenie albo całe mieszkanie: ściany, sufit, odliczenia za drzwi i nietypowe otwory.'],
              ['Bruk / kostka', 'Tryb terenowy: powierzchnia do ułożenia albo długość × szerokość, obrzeża, podbudowa i transport do ustalenia.'],
              ['Płytki (szybka wycena)', 'Orientacyjna robocizna układania płytek. To nie jest pełny kosztorys łazienki.'],
            ].map(([title, desc]) => (
              <div key={title} className="border border-white/10 bg-[#15191f] p-6">
                <h3 className="text-lg font-bold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#11151b] py-14 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold md:text-3xl">Jak działa Tarcza Marży</h2>
          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-slate-300">
            <p>
              Po policzeniu roboty Tarcza Marży pokazuje listę pozycji, które często giną w wycenach:
              gruntowanie, zabezpieczenie powierzchni, odpady, docinki, a przy bruku także korytowanie,
              podbudowę czy obrzeża.
            </p>
            <p>
              Każdą pozycję możesz dodać do ceny, oznaczyć jako sprawdzoną albo pominąć. Do oferty i PDF
              trafiają tylko pozycje, które sam dodasz. Nic nie jest doliczane automatycznie.
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold md:text-3xl">Dlaczego nie pokazujemy sztywnego cennika?</h2>
          <p className="mx-auto mt-5 max-w-3xl text-center text-slate-400">
            Remont nie jest jedną liczbą z internetu. Cena zależy od zakresu, podłoża, materiałów,
            detali, utrudnień i decyzji wykonawcy. ProWycena nie narzuca stawki — pomaga sprawdzić,
            czy w ofercie nie brakuje ważnych pozycji.
          </p>
          <div className="mx-auto mt-8 grid max-w-4xl gap-3 md:grid-cols-2">
            {[
              'Metraż i zakres zatwierdza wykonawca.',
              'Stawki są edytowalne przed wygenerowaniem oferty.',
              'Tarcza Marży pokazuje pozycje do sprawdzenia.',
              'Nic nie dolicza się bez akceptacji wykonawcy.',
              'PDF pokazuje czystą ofertę dla klienta.',
              'Zdjęcie jest kontekstem, nie miarką.',
            ].map((item) => (
              <div key={item} className="flex gap-3 border border-white/10 bg-[#15191f] p-4 text-sm text-slate-200">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="mx-auto mt-8 max-w-4xl border border-amber-400/30 bg-[#14120d] p-5">
            <p className="text-sm font-black uppercase tracking-[0.14em] text-amber-200">Pozycje, o których ProWycena może przypomnieć</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                'strefy mokre i hydroizolacja',
                'instalacje i punkty do przeróbki',
                'zabezpieczenia, odpady i sprzątanie',
                'detale, docinki i utrudnienia',
              ].map((item) => (
                <span key={item} className="border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-sm text-amber-50">
                  {item}
                </span>
              ))}
            </div>
            <h3 className="mt-5 text-base font-bold text-white">ProWycena przypomina, ale wykonawca decyduje</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              ProWycena porządkuje zakres i przypomina o pytaniach kontrolnych. Nie mierzy ze zdjęcia, nie ustala ceny
              i nie wysyła nic do PDF bez decyzji wykonawcy. Wycena liczy wyłącznie pozycje zatwierdzone przez wykonawcę.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#11151b] py-14 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold md:text-3xl">Czym ProWycena nie jest</h2>
          <ul className="mx-auto mt-6 max-w-3xl space-y-3 text-slate-300">
            <li className="border border-white/10 bg-[#15191f] p-4">
              Nie mierzy pomieszczeń ze zdjęcia. Zdjęcie pomaga zrozumieć kontekst — metraż wpisuje i zatwierdza wykonawca.
            </li>
            <li className="border border-white/10 bg-[#15191f] p-4">
              Nie jest pełnym kosztorysem inwestorskim ani programem do przetargów.
            </li>
            <li className="border border-white/10 bg-[#15191f] p-4">
              Nie podejmuje decyzji za wykonawcę. Każda stawka i każda pozycja z Tarczy Marży wymaga jego zatwierdzenia.
            </li>
          </ul>
        </div>
      </section>

      <section id="pdf-preview" className="border-y border-white/10 bg-[#11151b] py-14 md:py-20">
        <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-extrabold md:text-3xl">Gotowa oferta PDF dla klienta</h2>
            <p className="mt-4 text-slate-400">
              {"Wersja beta pokazuje robocz\u0105 wycen\u0119, pozycje do sprawdzenia i czysty dokument dla klienta. PDF nie pokazuje wewn\u0119trznych alert\u00f3w ani mar\u017cy wykonawcy."}
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-3">
              {benefitCards.map((card) => (
                <div key={card.title} className="border border-white/10 bg-[#15191f] p-4">
                  <card.icon className="mb-3 h-6 w-6 text-amber-300" />
                  <h3 className="font-bold text-white">{card.title}</h3>
                  <p className="mt-2 text-sm text-slate-400">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-white/10 bg-[#0b0d10] p-4">
            <div className="bg-white p-5 text-slate-900">
              <div className="mb-5 border-b border-slate-200 pb-4">
                <p className="text-sm font-bold text-slate-500">Oferta wykonawcy</p>
                <h3 className="text-2xl font-black">Malowanie pokoju</h3>
              </div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {[
                    ["Zakres", "Malowanie 2x"],
                    ["Powierzchnia", "do potwierdzenia"],
                    ["Cena dla klienta", "roboczo"],
                  ].map(([label, value]) => (
                    <tr key={label} className="border-b border-slate-100">
                      <td className="py-2 text-slate-600">{label}</td>
                      <td className="py-2 text-right font-bold">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-5 bg-slate-100 p-3 text-xs text-slate-600">
                {"Wycena ma charakter roboczy i wymaga potwierdzenia zakresu oraz powierzchni przed rozpocz\u0119ciem prac."}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="mx-auto max-w-2xl border border-emerald-400/25 bg-emerald-400/10 p-8">
            <h2 className="text-2xl font-extrabold md:text-3xl">{"Beta: pierwsze wyceny testowe bez op\u0142aty"}</h2>
            <p className="mt-4 text-slate-300">
              {"ProWycena jest w kontrolowanych testach z wykonawcami. Docelowo pojawi\u0105 si\u0119 pakiety dla firm po zako\u0144czeniu test\u00f3w."}
            </p>
            <Link
              to="/app/wycena"
              className="mt-7 inline-flex min-h-14 items-center justify-center rounded-md bg-emerald-500 px-7 font-black text-[#07110b] transition hover:bg-emerald-400"
            >
              {"Zr\u00f3b pierwsz\u0105 wycen\u0119"}
            </Link>
          </div>
        </div>
      </section>

      <section id="kontakt" className="border-y border-white/10 bg-[#11151b] py-14 md:py-16">
        <div className="container mx-auto grid gap-6 px-4 md:grid-cols-[0.8fr_1.2fr] md:items-center">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">Kontakt</p>
            <h2 className="mt-2 text-2xl font-black text-white">Masz pytanie przed testem?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-400">
              {"Napisz, je\u015bli chcesz sprawdzi\u0107 ProWycen\u0119 na realnej roboczej wycenie albo zg\u0142osi\u0107 b\u0142\u0105d z testu."}
            </p>
          </div>
          <div className="border border-[#2a2f38] bg-[#0b0d10] p-4">
            <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">E-mail</p>
                <a href={CONTACT_MAILTO} className="mt-1 block break-all text-lg font-black text-white">
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <a
                  href={CONTACT_MAILTO}
                  className="inline-flex min-h-14 items-center justify-center border border-amber-400/45 bg-[#15191f] px-5 text-sm font-black text-amber-100 transition hover:bg-[#1b2028]"
                >
                  Napisz e-mail
                </a>
                <button
                  type="button"
                  onClick={() => void handleCopyContactEmail()}
                  className="inline-flex min-h-14 items-center justify-center border border-slate-500/60 bg-[#15191f] px-5 text-sm font-black text-slate-100 transition hover:bg-[#1b2028]"
                >
                  Kopiuj adres
                </button>
              </div>
            </div>
            <p
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="mt-3 min-h-5 text-sm font-semibold text-amber-100"
            >
              {copyMessage}
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="container mx-auto grid gap-6 px-4 lg:grid-cols-2">
          <div id="regulamin" className="border border-[#2a2f38] bg-[#11151b] p-6">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">Regulamin</p>
            <h2 className="mt-2 text-2xl font-black text-white">Regulamin wersji testowej</h2>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-300">
              <li>{"ProWycena generuje robocze wyceny do potwierdzenia przez wykonawc\u0119."}</li>
              <li>{"Metra\u017c, zakres i ko\u0144cow\u0105 cen\u0119 zatwierdza u\u017cytkownik."}</li>
              <li>{"Aplikacja nie zast\u0119puje kosztorysanta, rzeczoznawcy ani pomiaru na miejscu."}</li>
              <li>{"PDF jest dokumentem ofertowym do potwierdzenia przed rozpocz\u0119ciem prac."}</li>
            </ul>
          </div>

          <div id="polityka-prywatnosci" className="border border-[#2a2f38] bg-[#11151b] p-6">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">{"Prywatno\u015b\u0107"}</p>
            <h2 className="mt-2 text-2xl font-black text-white">{"Polityka prywatno\u015bci wersji testowej"}</h2>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-300">
              <li>Administrator: ProWycenaapp.pl, kontakt: prowycena.app@gmail.com.</li>
              <li>{"Przetwarzane dane: konto, projekty, wyceny i dane firmy wpisane przez u\u017cytkownika."}</li>
              <li>{"Dane s\u0142u\u017c\u0105 do dzia\u0142ania aplikacji i generowania ofert."}</li>
              <li>{"U\u017cytkownik mo\u017ce poprosi\u0107 o usuni\u0119cie danych przez kontakt e-mail."}</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#080a0d] py-8">
        <div className="container mx-auto flex flex-col gap-4 px-4 text-sm text-slate-400 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="font-bold text-slate-200">ProWycenaapp.pl</p>
            <p>ProWycena jest projektem beta testowanym z wykonawcami.</p>
            <p>{"Wyceny maj\u0105 charakter roboczy i wymagaj\u0105 potwierdzenia zakresu oraz powierzchni przed rozpocz\u0119ciem prac."}</p>
            <p className="mt-2">
              Kontakt:{" "}
              <a href="mailto:prowycena.app@gmail.com" className="font-semibold text-amber-200 hover:underline">
                prowycena.app@gmail.com
              </a>
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/o-produkcie" className="hover:text-amber-200">O produkcie</Link>
            <Link to="/tarcza-marzy" className="hover:text-amber-200">Tarcza Marży</Link>
            <Link to="/faq" className="hover:text-amber-200">FAQ</Link>
            <Link to="/kontakt" className="hover:text-amber-200">Kontakt</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-amber-200">{"Polityka prywatno\u015bci"}</Link>
            <Link to="/regulamin" className="hover:text-amber-200">Regulamin testów beta</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
